import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Bell } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { buildInviteAuthSearch } from '../../lib/authRedirect';
import { getPublicEnv } from '../../lib/publicEnv';
import { useAuthStore } from '../../stores/authStore';
import { useSpaceStore } from '../../stores/spaceStore';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import toast from 'react-hot-toast';

const emojis = ['👤', '👋', '😊', '🤠', '👻', '🤖', '🎃', '🦄', '🐶', '🐱', '🐼', '🦁'];

export default function OnboardingJoin() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isPro } = useAuthStore();
  const { setCurrentSpace, joinSpace } = useSpaceStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [space, setSpace] = useState<any>(null);
  const [ownerInfo, setOwnerInfo] = useState<any>(null);
  const [spaceMembers, setSpaceMembers] = useState<any[]>([]);
  const _spaceMembers = spaceMembers; // reference to avoid TS warning
  void _spaceMembers;
  const [spaceCategories, setSpaceCategories] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    displayName: '',
    avatarEmoji: emojis[0]
  });
  const [budgets, setBudgets] = useState<Record<string, number>>({});
  const [pushEnabled, setPushEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    const fetchSpace = async () => {
      if (!token) return;
      const prefillName = searchParams.get('prefillName')?.trim();

      const response = await fetch(`/api/invites/${token}`);
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || !payload.space) {
        toast.error('Invalid invite link');
        navigate('/login');
        return;
      }

      setSpace(payload.space);
      setOwnerInfo(payload.ownerInfo || null);
      setSpaceMembers(payload.ownerInfo ? [payload.ownerInfo] : []);
      setSpaceCategories(payload.spaceCategories || []);

      const suggestedInviteeName = payload.suggestedInviteeName?.trim();
      if (payload.ownerInfo) {
        const owner = payload.ownerInfo;
        setOwnerInfo(owner);
        setFormData(prev => ({
          ...prev,
          displayName:
            prev.displayName ||
            prefillName ||
            suggestedInviteeName ||
            `Partner of ${owner.display_name}`
        }));
      } else if (prefillName || suggestedInviteeName) {
        setFormData(prev => ({
          ...prev,
          displayName: prev.displayName || prefillName || suggestedInviteeName || ''
        }));
      }
      setIsLoading(false);
    };

    fetchSpace();
  }, [token, navigate, searchParams]);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.displayName.trim()) {
      toast.error('Please enter your display name');
      return;
    }

    setIsJoining(true);
    try {
      if (!user) {
        navigate(`/login?${buildInviteAuthSearch({
          token: token!,
          displayName: formData.displayName,
          spaceName: space?.name,
        })}`);
        return;
      }

      await joinSpace(token!, formData.displayName.trim(), formData.avatarEmoji);

      setCurrentSpace(space);

      if (isPro) {
        for (const [categoryId, amountDollars] of Object.entries(budgets)) {
          if (amountDollars > 0) {
            await supabase.from('budgets').insert({
              category_id: categoryId,
              space_id: space.id,
              amount_cents: Math.round(amountDollars * 100),
              period: 'monthly'
            });
          }
        }
      }

      if (pushEnabled) {
        try {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            if ('serviceWorker' in navigator && 'PushManager' in window) {
              const registration = await navigator.serviceWorker.ready;
              const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(
                  getPublicEnv('VITE_VAPID_PUBLIC_KEY') || 'demo-vapid-key'
                )
              });
              await fetch('/api/push/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ subscription, spaceId: space.id })
              });
              toast.success('🔔 Notifications enabled!');
            }
          }
        } catch {
          // Push setup failed - continue silently
        }
      }

      toast.success(`Welcome to ${space.name}!`);
      navigate('/');
    } catch (err: any) {
      toast.error(err.message || 'Failed to join space');
    } finally {
      setIsJoining(false);
    }
  };

  const handlePushToggle = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications not supported');
      return;
    }
    const permission = await Notification.requestPermission();
    setPushEnabled(permission === 'granted');
    if (permission === 'granted') {
      toast.success('Notifications enabled!');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse w-12 h-12 bg-primary/20 rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col px-6 py-8 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex-1 flex flex-col max-w-md mx-auto w-full"
      >
        <div className="mb-6 text-center">
          <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🎉</span>
          </div>
          <h1 className="text-2xl font-bold mb-2">You've been invited!</h1>
          <p className="text-muted">Join <strong>{space?.name}</strong> to start tracking expenses together</p>
        </div>

        <form onSubmit={handleJoin} className="flex-1 flex flex-col space-y-4 overflow-y-auto">
          {/* Profile */}
          <div className="bg-card rounded-2xl p-4 border border-border">
            <h3 className="font-semibold mb-3">Your Profile</h3>
            <div className="flex justify-center gap-2 flex-wrap mb-3">
              {emojis.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setFormData({ ...formData, avatarEmoji: emoji })}
                  className={`text-2xl p-2 rounded-lg transition-colors ${
                    formData.avatarEmoji === emoji
                      ? 'bg-primary/20 ring-2 ring-primary'
                      : 'hover:bg-muted/10'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
            <Input
              label="Display Name"
              placeholder="e.g., Alex"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              maxLength={50}
              required
            />
          </div>

          {/* Categories Overview */}
          {spaceCategories.length > 0 && (
            <div className="bg-card rounded-2xl p-4 border border-border">
              <h3 className="font-semibold mb-3">Category Setup</h3>
              <p className="text-xs text-muted mb-3">Here's how {ownerInfo?.display_name || 'the owner'} set things up</p>
              <div className="grid grid-cols-2 gap-2">
                {spaceCategories.slice(0, 8).map(cat => {
                  const fixedConfig = cat.fixed_configs?.[0];
                  const defaults = cat.category_defaults?.[0];
                  return (
                    <div key={cat.id} className="flex items-center gap-2 p-2 bg-background rounded-lg">
                      <span className="text-lg">{cat.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{cat.name}</p>
                        <div className="flex gap-1 mt-0.5">
                          {fixedConfig && <Badge variant="fixed" size="sm">FIXED</Badge>}
                          {defaults && (
                            <Badge variant={defaults.action === 'split' ? 'split' : 'keep'} size="sm">
                              {defaults.action === 'split' ? `${defaults.split_ratio}%` : 'KEEP'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {spaceCategories.length > 8 && (
                <p className="text-xs text-muted mt-2 text-center">+{spaceCategories.length - 8} more categories</p>
              )}
            </div>
          )}

          {/* Budget per category (Pro-only) */}
          {spaceCategories.length > 0 && (
            <div className="bg-card rounded-2xl p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Monthly Budget</h3>
                {!isPro && (
                  <span className="text-xs text-muted flex items-center gap-1">
                    <Crown size={12} />
                    Pro
                  </span>
                )}
              </div>
              {isPro ? (
                <div className="space-y-3">
                  {spaceCategories.slice(0, 6).map(cat => (
                    <div key={cat.id} className="flex items-center gap-3">
                      <span className="text-sm w-8 text-center">{cat.emoji}</span>
                      <span className="text-xs text-muted flex-1 truncate">{cat.name}</span>
                      <div className="relative w-24">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted text-xs">$</span>
                        <input
                          type="number"
                          value={budgets[cat.id] || ''}
                          onChange={(e) => setBudgets(prev => ({
                            ...prev,
                            [cat.id]: Math.max(0, parseFloat(e.target.value) || 0)
                          }))}
                          className="w-full bg-background border border-border rounded-lg py-1.5 pl-5 pr-2 text-sm text-foreground"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-muted text-center">Set optional budget caps per category</p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => toast('👑 Upgrade to Pro for budget tracking', { icon: '👑' })}
                  className="w-full py-2 px-3 bg-muted/10 rounded-lg text-muted text-sm cursor-not-allowed"
                >
                  Locked — upgrade to Pro
                </button>
              )}
            </div>
          )}

          {/* Push Notifications */}
          <div className="bg-card rounded-2xl p-4 border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  pushEnabled === true ? 'bg-primary/20' : 'bg-muted/10'
                }`}>
                  <Bell size={20} className={pushEnabled === true ? 'text-primary' : 'text-muted'} />
                </div>
                <div>
                  <h3 className="font-medium text-sm">Notifications</h3>
                  <p className="text-xs text-muted">
                    {pushEnabled === true ? 'Enabled ✓' : pushEnabled === false ? 'Declined' : 'Stay updated on expenses'}
                  </p>
                </div>
              </div>
              {pushEnabled !== true && (
                <button
                  type="button"
                  onClick={handlePushToggle}
                  className="px-4 py-2 bg-primary text-background rounded-lg text-sm font-medium touch-target"
                >
                  Enable
                </button>
              )}
            </div>
          </div>

          <div className="flex-1" />

          <Button
            type="submit"
            disabled={isJoining}
            className="w-full"
          >
            {isJoining ? 'Joining...' : `Join ${space?.name}`}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return new Uint8Array([...rawData].map(char => char.charCodeAt(0)));
}
